import { useMemo } from 'react';
import type { Options } from 'highcharts';
import { Chart } from '@forge/charts';
import { useTheme } from '@forge/common';
import type { DashboardTrendPoint } from '@/api/dashboard';
import ChartCard from './ChartCard';
import { getAccentColors, getStatusColors } from './chartColors';

interface ProgressTrendChartProps {
  points: DashboardTrendPoint[];
}

const ProgressTrendChart = ({ points }: ProgressTrendChartProps) => {
  const { isDark } = useTheme();

  const options = useMemo<Options>(() => {
    const accents = getAccentColors(isDark);
    const status = getStatusColors(isDark);

    return {
      chart: { height: 320, spacing: [16, 16, 12, 8] },
      title: { text: undefined },
      xAxis: { categories: points.map((point) => point.label), tickLength: 0 },
      yAxis: {
        title: { text: undefined },
        allowDecimals: false,
        gridLineWidth: 1,
      },
      legend: { align: 'left', verticalAlign: 'top', symbolRadius: 6 },
      tooltip: { shared: true },
      plotOptions: {
        areaspline: {
          fillOpacity: 0.16,
          lineWidth: 3,
          marker: { enabled: false, symbol: 'circle' },
        },
        column: { borderRadius: 4, maxPointWidth: 26 },
      },
      series: [
        {
          type: 'areaspline',
          name: 'Cumulative completed',
          data: points.map((point) => point.cumulativeCompleted),
          color: accents.blue,
        },
        {
          type: 'column',
          name: 'Completed this month',
          data: points.map((point) => point.milestonesCompleted),
          color: status.done,
        },
      ],
    };
  }, [points, isDark]);

  return (
    <ChartCard
      title="Delivery momentum"
      subtitle="Milestone completion trend across the selected window"
    >
      <Chart options={options} />
    </ChartCard>
  );
};

export type { ProgressTrendChartProps };
export default ProgressTrendChart;
